import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCellarStore } from '../store/CellarStoreContext';
import {
  findWineByIdentity,
  recordPurchase,
  type RecordPurchaseInput,
  type TypeDeVin,
  type Wine,
} from '../domain/cellar';
import {
  WineIdentityFields,
  emptyWineIdentity,
  type WineIdentityFormValue,
} from '../components/WineIdentityFields';
import { DRINK_BY_YEARS_AHEAD, DRINK_BY_YEARS_BACK, YearPicker } from '../components/YearPicker';
import { BackButton } from '../components/BackButton';
import {
  PurchaseFields,
  defaultPurchaseFields,
  purchaseInputFrom,
  type PurchaseFormValue,
} from '../components/PurchaseFields';
import { WineMatchPrompt } from '../components/WineMatchPrompt';
import { LabelScanControl } from '../components/LabelScanControl';
import { WebCompletion, type CompletionOutcome } from '../components/WebCompletion';
import { ratingKey } from '../components/OnlineRatingControl';
import { onlineRatingFrom, type SavedOnlineRating } from '../domain/online-rating';
import { todayInParis } from '../domain/drink-by';
import { canonicalCepage, grapeChoices } from '../domain/grapes';
import { applyCompletion, applyScan, type ScanFields } from '../assist/client';
import type { LabelPhotos } from '../domain/label-photos';
import { keepPhoto } from '../photos/keep-photo';
import { useBackend } from '../backend/BackendContext';
import { quotaMessage, t, tf } from '../i18n';
import './NewPurchasePage.css';
import { userMessage } from '../i18n/errors';

/**
 * /c/:cellarId/achat/nouveau (Story 1, FR-007-FR-010): pick an existing
 * wine or describe a new one, an optional drink-by year, and the
 * purchase itself, in one flow. At the top (owner, 2026-10-06), "Scanner la bouteille" (User
 * Story 8, T127) pre-fills the empty fields from a label photo with its confident values, then
 * completes the wine from the web in the same tap; "Recherche en ligne" (User Story 9, as changed
 * by the owner on 2026-10-03) does that completion alone: the empty wine fields and the drink-by
 * year from one web search, with the wine's online rating, saved with the purchase. Nothing is
 * saved before "Enregistrer".
 */
export function NewPurchasePage() {
  const { cellarId } = useParams<{ cellarId: string }>();
  const navigate = useNavigate();
  const { cellar, apply } = useCellarStore();

  const [identity, setIdentity] = useState<WineIdentityFormValue>(emptyWineIdentity());
  const [drinkUntilYear, setDrinkUntilYear] = useState('');
  const [purchase, setPurchase] = useState<PurchaseFormValue>(defaultPurchaseFields());
  const [error, setError] = useState<string | undefined>();
  const [pendingMatch, setPendingMatch] = useState<Wine | undefined>();
  // User Story 8: a label scan pre-fills the form's empty fields with its confident values.
  const { assist, photos } = useBackend();
  // The label photos of the last scan, kept with the wine when it is saved (owner, 2026-10-11).
  const [scanPhotos, setScanPhotos] = useState<string[]>([]);
  const [scanned, setScanned] = useState(false);
  // The online rating the web completion found is saved with the purchase, if it is for the wine
  // being saved.
  const [onlineRating, setOnlineRating] = useState<
    { key: string; saved: SavedOnlineRating } | undefined
  >();
  const [completing, setCompleting] = useState(false);
  const [completion, setCompletion] = useState<
    { outcome: CompletionOutcome; searchSuggestions?: string } | undefined
  >();
  // The scan and the web search answer after a moment: they must fill the form as it is then,
  // not as it was.
  const identityNow = useRef(identity);
  identityNow.current = identity;
  const drinkUntilNow = useRef(drinkUntilYear);
  drinkUntilNow.current = drinkUntilYear;

  function takeDraft(fields: ScanFields) {
    const draft = applyScan(identityNow.current, fields);
    identityNow.current = draft;
    setIdentity(draft);
    setScanned(true);
    // The same tap goes on to complete the wine from the web (owner, 2026-10-03).
    void complete(draft);
  }

  /** One web search fills the empty fields and finds the online rating (owner, 2026-10-03). */
  async function complete(from: WineIdentityFormValue) {
    if (!assist || from.domaine.trim() === '') return;
    setCompleting(true);
    setCompletion(undefined);
    const result = await assist.complete(from);
    setCompleting(false);
    if (!result.ok) {
      const message = 'reason' in result ? quotaMessage(result.period) : t('complete.unavailable');
      setCompletion({ outcome: { kind: 'failed', message } });
      return;
    }
    const filled = applyCompletion(identityNow.current, drinkUntilNow.current, result.fields);
    setIdentity(filled.identity);
    setDrinkUntilYear(filled.drinkUntilYear);
    const saved = onlineRatingFrom(result.ratings, todayInParis());
    if (saved) setOnlineRating({ key: ratingKey(filled.identity), saved });
    setCompletion({
      outcome: { kind: filled.filled || saved ? 'done' : 'none' },
      ...(result.searchSuggestions ? { searchSuggestions: result.searchSuggestions } : {}),
    });
  }

  function editIdentity(next: WineIdentityFormValue) {
    setIdentity(next);
  }

  const thisYear = todayInParis().year;
  const grapes = useMemo(() => grapeChoices(cellar?.wines ?? []), [cellar]);
  const annee = Number(identity.annee);
  const identityIsComplete =
    identity.domaine.trim() !== '' && identity.cepage.trim() !== '' && Number.isInteger(annee);

  const existingMatch = useMemo(() => {
    if (!cellar || !identityIsComplete) {
      return undefined;
    }
    return findWineByIdentity(cellar, {
      domaine: identity.domaine,
      cepage: identity.cepage,
      parcelle: identity.parcelle,
      annee,
    });
  }, [cellar, identity, identityIsComplete, annee]);

  /** Only called once the caller has checked Pays, Région and typeDeVin are non-empty. */
  function buildInput(): RecordPurchaseInput {
    const wine = {
      domaine: identity.domaine.trim(),
      cepage: canonicalCepage(identity.cepage, grapes),
      ...(identity.parcelle.trim() !== '' ? { parcelle: identity.parcelle.trim() } : {}),
      annee,
      pays: identity.pays,
      region: identity.region,
      typeDeVin: identity.typeDeVin as TypeDeVin,
      ...(drinkUntilYear !== '' ? { drinkUntilYear: Number(drinkUntilYear) } : {}),
    };
    return {
      wine,
      // With, for a wine tasted elsewhere (quantity 0, owner 2026-10-11), its rating and comment.
      ...purchaseInputFrom(purchase),
      ...(onlineRating && onlineRating.key === ratingKey(identity)
        ? { onlineRating: onlineRating.saved }
        : {}),
    };
  }

  /** Actually records the purchase. Never itself checks for a match — the caller already did. */
  async function doSubmit() {
    try {
      const input = buildInput();
      const labelPhotos = await keepScanPhotos();
      await apply(recordPurchase({ ...input, ...(labelPhotos ? { labelPhotos } : {}) }));
      // A wine with no bottles is only listed with the finished ones.
      navigate(`/c/${cellarId}/stock${input.quantity === 0 ? '?epuises=1' : ''}`);
    } catch (err) {
      setError(userMessage(err));
    }
  }

  /**
   * Stores the scanned photos next to the cellar, front then back. A photo that cannot be stored
   * is left out: the purchase still saves (owner request, 2026-10-11).
   */
  async function keepScanPhotos(): Promise<LabelPhotos | undefined> {
    if (scanPhotos.length === 0 || !cellarId) return undefined;
    const sides = ['front', 'back'] as const;
    const kept: LabelPhotos = {};
    for (const [index, image] of scanPhotos.slice(0, 2).entries()) {
      try {
        kept[sides[index]!] = await keepPhoto(photos, cellarId, image);
      } catch {
        // Not kept; the wine's page can take it again.
      }
    }
    return kept.front || kept.back ? kept : undefined;
  }

  /** The form's submit handler: validates, and pauses for confirmation on an identity match. */
  async function handleFormSubmit() {
    setError(undefined);
    if (!identity.cepage.trim() || !identity.pays || !identity.region || !identity.typeDeVin) {
      setError(t('purchase.missingFields'));
      return;
    }
    if (existingMatch) {
      setPendingMatch(existingMatch);
      return;
    }
    await doSubmit();
  }

  return (
    <main>
      <header className="page-title">
        <BackButton home={`/c/${cellarId}`} />
        <h1>{t('purchase.title')}</h1>
      </header>
      {error && <p role="alert">{error}</p>}

      {pendingMatch ? (
        <WineMatchPrompt
          wine={pendingMatch}
          onConfirmAddToExisting={() => {
            setPendingMatch(undefined);
            void doSubmit();
          }}
          onKeepEditing={() => setPendingMatch(undefined)}
        />
      ) : (
        <form
          className="purchase-form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleFormSubmit();
          }}
        >
          {assist && (
            <div className="purchase-tools">
              <LabelScanControl client={assist} onDraft={takeDraft} onPhotos={setScanPhotos} />
              <WebCompletion
                busy={completing}
                disabled={identity.domaine.trim() === ''}
                onComplete={() => void complete(identity)}
                outcome={completion?.outcome}
                rating={
                  onlineRating && onlineRating.key === ratingKey(identity)
                    ? onlineRating.saved
                    : undefined
                }
                searchSuggestions={completion?.searchSuggestions}
              />
            </div>
          )}
          {scanned && existingMatch && (
            <p role="status">
              {tf('scan.matchesExisting', {
                wine: `${existingMatch.domaine} ${existingMatch.annee}`,
              })}
            </p>
          )}
          <WineIdentityFields value={identity} onChange={editIdentity} grapes={grapes}>
            <label htmlFor="drinkUntilYear">{t('drinkUntil.legend')}</label>
            <YearPicker
              buttonId="drinkUntilYear"
              value={drinkUntilYear}
              onChange={setDrinkUntilYear}
              from={thisYear - DRINK_BY_YEARS_BACK}
              to={thisYear + DRINK_BY_YEARS_AHEAD}
              suggested={thisYear + 10}
            />
          </WineIdentityFields>
          <PurchaseFields value={purchase} onChange={setPurchase} />
          <button type="submit">{t('purchase.submit')}</button>
        </form>
      )}
    </main>
  );
}
